/**
 * Service for managing Matrix API operations
 * Implements all endpoints from the Matrix API Frontend Integration Guide
 */

import { makeAuthenticatedRequest } from './backendApiService';
import { convertUTCDatesToLocal } from '../utils/dateUtils';

const MATRIX_BASE_URL = '/api/matrices';

/**
 * Helper to convert dates in matrix objects from UTC to local
 * @param {Object|Array} data - Matrix or array of matrices
 * @returns {Object|Array} Data with converted dates
 */
const convertMatrixDates = (data) => {
    return convertUTCDatesToLocal(data, ['createdDate', 'lastMod']);
};

/**
 * Matrix Service Class
 */
class MatrixService {
    /**
     * Get all matrices (active and inactive)
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of matrix objects
     */
    async getAll(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(accessToken, MATRIX_BASE_URL);
            return convertMatrixDates(response);
        } catch (error) {
            console.error('Failed to fetch all matrices:', error);
            throw error;
        }
    }

    /**
     * Get only active matrices
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of active matrix objects
     */
    async getActive(accessToken) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken, 
                `${MATRIX_BASE_URL}?active=true`
            );
            return convertMatrixDates(response);
        } catch (error) {
            console.error('Failed to fetch active matrices:', error);
            throw error;
        }
    }

    /**
     * Get matrices by type
     * @param {string} accessToken - JWT access token
     * @param {string} type - Matrix type (e.g., "Liquid", "Solid", "Air")
     * @returns {Promise<Array>} Array of matrix objects matching the specified type
     */
    async getByType(accessToken, type) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${MATRIX_BASE_URL}?type=${encodeURIComponent(type)}`
            );
            return convertMatrixDates(response);
        } catch (error) {
            console.error(`Failed to fetch matrices by type ${type}:`, error);
            throw error;
        }
    }

    /**
     * Get matrix by ID
     * @param {string} accessToken - JWT access token
     * @param {number} id - Matrix ID
     * @returns {Promise<Object>} Matrix object
     */
    async getById(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${MATRIX_BASE_URL}/${id}`
            );
            return convertMatrixDates(response);
        } catch (error) {
            console.error(`Failed to fetch matrix with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get matrix by code
     * @param {string} accessToken - JWT access token
     * @param {string} code - Matrix code
     * @returns {Promise<Object>} Matrix object
     */
    async getByCode(accessToken, code) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${MATRIX_BASE_URL}/code/${encodeURIComponent(code)}`
            );
            return convertMatrixDates(response);
        } catch (error) {
            console.error(`Failed to fetch matrix with code ${code}:`, error);
            throw error;
        }
    }

    /**
     * Create a new matrix
     * @param {string} accessToken - JWT access token
     * @param {Object} matrixData - Matrix data to create
     * @returns {Promise<Object>} Created matrix object with ID
     */
    async create(accessToken, matrixData) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                MATRIX_BASE_URL,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(matrixData),
                }
            );
            return convertMatrixDates(response);
        } catch (error) {
            console.error('Failed to create matrix:', error);
            throw error;
        }
    }

    /**
     * Update an existing matrix
     * @param {string} accessToken - JWT access token
     * @param {number} id - Matrix ID
     * @param {Object} matrixData - Updated matrix data
     * @returns {Promise<Object>} Updated matrix object
     */
    async update(accessToken, id, matrixData) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${MATRIX_BASE_URL}/${id}`,
                {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(matrixData),
                }
            );
            return convertMatrixDates(response);
        } catch (error) {
            console.error(`Failed to update matrix with ID ${id}:`, error);
            throw error;
        }
    }

    /**
     * Delete a matrix
     * @param {string} accessToken - JWT access token
     * @param {number} id - Matrix ID
     * @returns {Promise<Object>} Success message
     */
    async delete(accessToken, id) {
        try {
            const response = await makeAuthenticatedRequest(
                accessToken,
                `${MATRIX_BASE_URL}/${id}`,
                {
                    method: 'DELETE',
                }
            );
            return response;
        } catch (error) {
            console.error(`Failed to delete matrix with ID ${id}:`, error);
            throw error;
        }
    }
}

// Export a singleton instance
const matrixService = new MatrixService();
export default matrixService;
